import { useEffect, useRef, useState } from 'react';
import { GridState, GridTopology } from '../types';
import { BezierCurve } from '../bezier/bezier';
import { LMPSurfaceResult, LMPSurfaceHour } from '../powerFlow/lmpSurface';
import type { LMPSurfaceRequest, LMPSurfaceResponse } from '../workers/lmpWorker';
import type { RuleProgram } from '../powerFlow/language/ruleSet';
import { logger } from '../utils/logger';

interface Params {
  busId: string | null;
  gridTopology: GridTopology | null;
  loadCurve: BezierCurve;
  windCurve?: BezierCurve;
  solarCurve?: BezierCurve;
  numTimeSteps: number;
  requireMatchingSOC: boolean;
  /** The compiled rule set to solve against, so the surface tracks rule edits. */
  program?: RuleProgram;
}

const PRICE_MAX = 100;

/**
 * Compute the LMP surface for the selected bus in a web worker, so the
 * synchronous WASM solves don't block the main thread. Results stream back per
 * hour (progressive rendering); stale requests are superseded by reqId.
 */
export function useLMPSurfaceWorker(params: Params): { result: LMPSurfaceResult | null; loading: boolean } {
  const { busId, gridTopology, loadCurve, windCurve, solarCurve, numTimeSteps, requireMatchingSOC, program } = params;

  const workerRef = useRef<Worker | null>(null);
  const reqIdRef = useRef(0);
  const hoursRef = useRef<LMPSurfaceHour[]>([]);
  const busRef = useRef<string | null>(null);

  const [result, setResult] = useState<LMPSurfaceResult | null>(null);
  const [loading, setLoading] = useState(false);

  // Lazily create the singleton worker with its message handler.
  const getWorker = (): Worker => {
    if (!workerRef.current) {
      const worker = new Worker('lmpWorker.js');
      worker.onmessage = (e: MessageEvent<LMPSurfaceResponse>) => {
        const msg = e.data;
        if (!msg || msg.reqId !== reqIdRef.current) return; // stale/superseded
        if (msg.type === 'progress') {
          hoursRef.current = [...hoursRef.current, msg.hour];
          const hrs = hoursRef.current;
          setResult({
            busId: busRef.current ?? '',
            isLoad: hrs.some((h) => h.loadMW != null),
            hours: hrs,
            maxDemandMW: Math.max(1, ...hrs.map((h) => h.feasibleMaxMW), ...hrs.map((h) => h.loadMW ?? 0)),
            priceMax: PRICE_MAX,
          });
        } else if (msg.type === 'done') {
          setResult(msg.result);
          setLoading(false);
        } else if (msg.type === 'error') {
          logger.error('LMP surface worker error:', msg.message);
          setResult(null);
          setLoading(false);
        }
      };
      worker.onerror = (e) => {
        logger.error('LMP surface worker crashed:', e.message);
        setLoading(false);
      };
      workerRef.current = worker;
    }
    return workerRef.current;
  };

  // Terminate the worker on unmount.
  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  // Fire a fresh computation whenever the inputs change.
  useEffect(() => {
    if (!busId || !gridTopology) {
      reqIdRef.current++; // supersede any in-flight request
      setResult(null);
      setLoading(false);
      return;
    }

    const reqId = ++reqIdRef.current;
    hoursRef.current = [];
    busRef.current = busId;
    setResult(null);
    setLoading(true);

    const gs: GridState = {
      nodes: gridTopology.nodes.map((node) => ({ ...node, value: 0 })),
      edges: gridTopology.edges.map((edge) => ({ ...edge, current: 0 })),
    };

    const request: LMPSurfaceRequest = {
      type: 'compute',
      reqId,
      gridTopology: gs,
      loadCurve,
      windCurve,
      solarCurve,
      numTimeSteps,
      requireMatchingSOC,
      busId,
      program,
    };
    getWorker().postMessage(request);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busId, gridTopology, loadCurve, windCurve, solarCurve, numTimeSteps, requireMatchingSOC, program]);

  return { result, loading };
}
